import { shaderMaterial, Sphere } from "@react-three/drei";
import { extend, useFrame } from "@react-three/fiber";
import { useControls } from "leva";
import { useRef } from "react";
import { AdditiveBlending, Color, DoubleSide, Uniform } from "three";
import { useGSAP } from "@gsap/react";
import { gsap } from "gsap";
import { useSelector } from "react-redux";

const PanelMaterial = shaderMaterial(
  {
    uFallOffThreshold: 0,
    uTexture: null,
    uTime: 0,
    uColor: new Color("#618ca9"),
    uFresnelStrength: 5.25,
    uPower: 5,
    uOpacity: 1,
  },
  `
  varying vec2 vUv;
  varying vec3 vPosition;
  varying vec3 vNormal;
      uniform float uTime;

  
  float random2D(vec2 value)
    {
        return fract(sin(dot(value.xy, vec2(12.9898,78.233))) * 43758.5453123);
    }
  
  void main() {
    vec4 modelPosition = modelMatrix * vec4(position, 1.0);

    float glitchTime = uTime-modelPosition.y;
    float glitchStrength = sin(glitchTime) + sin(glitchTime*3.45) + sin(glitchTime*8.76);
    glitchStrength /= 3.0;
    glitchStrength = smoothstep(0.,1.0,glitchStrength);
    glitchStrength *= 0.25;
    modelPosition.x += (random2D(modelPosition.xz + uTime)-.5)*glitchStrength;
    modelPosition.y += (random2D(modelPosition.zx + uTime)-.5)*glitchStrength;
    
    vec4 viewPosition = viewMatrix * modelPosition;
    vec4 projectionPosition = projectionMatrix * viewPosition;
    gl_Position = projectionPosition;
    
    vec4 modelNormal = modelMatrix * vec4(normal, 0.0);
    
    vPosition = modelPosition.rgb;
    vNormal =  modelNormal.rgb;
    vUv = uv;
  }`,
  `
    uniform float uFallOffThreshold;
    uniform float uTime;
    uniform float uFresnelStrength;
    uniform float uPower;
    uniform vec3 uColor;
    varying vec3 vPosition;
    varying vec3 vNormal;
    varying vec2 vUv;  
   
    void main() {
        if(uFallOffThreshold <= 0.001) {
            discard;
            return;
        }

        vec3 normal = normalize(vNormal);
        if(!gl_FrontFacing) {
            normal = -normal;
        }
    
        float stripeFrequency = 95.0;
        float stripeSpeed = 0.3;
        float stribe = mod((vPosition.y - uTime * stripeSpeed) * stripeFrequency, 1.);
        stribe = smoothstep(0.0, 0.3, stribe) * smoothstep(1.0, 0.7, stribe);
        
        vec3 viewDirection = normalize(vPosition - cameraPosition);
        float fresnel = dot(viewDirection, normal) + 1.;
        fresnel = pow(fresnel, uPower);
        
        float fallOff = smoothstep(uFallOffThreshold, 0.0, fresnel);
        
        float holographic = stribe * fresnel * 0.5;
        holographic += fresnel * uFresnelStrength;
        holographic *= fallOff;
        
        holographic = smoothstep(0.0, 1.0, holographic);
        holographic = min(holographic, 1.0);
        
        gl_FragColor = vec4(uColor.rgb, holographic * step(0.001, uFallOffThreshold));
        #include <tonemapping_fragment>
        #include <colorspace_fragment>
    }

`,
);

// shaderMaterial creates a THREE.ShaderMaterial, and auto-creates uniform setter/getters
// extend makes it available in JSX, in this case <portalMaterial />
extend({ PanelMaterial });
export default function HologramMaterial({ power = 2, uFresnelStrength, color }) {
  const currentModule = useSelector((state) => state.progression.currentModule);
  const material = useRef();

  useFrame((state, delta, frame) => {
    if (material.current) {
      material.current.uniforms.uTime.value += delta;
      material.current.uniforms.uColor.value.set(color);
    }
  });

  return (
    <panelMaterial
      transparent={true}
      ref={material}
      side={DoubleSide}
      depthWrite={false}
      blending={AdditiveBlending}
      uFresnelStrength={uFresnelStrength}
      uPower={power}
      uColor={color}
    ></panelMaterial>
  );
}
